import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker,
  Polyline, Polygon, useMap, useMapEvents, FeatureGroup,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { EditControl } from 'react-leaflet-draw';
import {
  MapPin, Search, Navigation, Footprints, X, Loader2, Crosshair, Heart, Eye, Pentagon,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet's default icon path (well-known react-leaflet issue with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER = [40.6782, -73.9442]; // Brooklyn, NY — NHF home base
const DEFAULT_ZOOM = 12;

// Make a colored marker by tinting a div via CSS — keeps it framework-light
const coloredIcon = (color) => L.divIcon({
  className: 'nhf-zone-marker',
  html: `<div style="
    width: 22px; height: 22px; border-radius: 50% 50% 50% 0;
    background: ${color || '#BA1826'}; transform: rotate(-45deg);
    border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.4);
  "></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
});

// Building/square icon for Locations (distinct from circular zone markers)
const locationIcon = (color, status) => {
  const fill = status === 'denied' ? '#DC2626'
    : status === 'approved' || status === 'completed' ? '#16A34A'
    : status === 'permission_needed' ? '#F59E0B'
    : status === 'follow_up_needed' ? '#9333EA'
    : (color || '#0A0A0A');
  return L.divIcon({
    className: 'nhf-location-marker',
    html: `<div style="
      width: 18px; height: 18px;
      background: ${fill}; border: 2px solid white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.4);
      display: flex; align-items: center; justify-content: center;
      color: white; font-size: 11px; font-weight: 700;
    ">■</div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });
};

const youAreHereIcon = L.divIcon({
  className: 'nhf-you-are-here',
  html: `<div style="
    width: 16px; height: 16px; border-radius: 50%;
    background: #3B82F6; border: 3px solid white;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.35), 0 1px 3px rgba(0,0,0,0.3);
    animation: pulseLoc 1.4s ease-out infinite;
  "></div><style>@keyframes pulseLoc { 0%{box-shadow:0 0 0 3px rgba(59,130,246,0.35),0 0 0 0 rgba(59,130,246,0.7);} 70%{box-shadow:0 0 0 3px rgba(59,130,246,0.35),0 0 0 14px rgba(59,130,246,0);} 100%{box-shadow:0 0 0 3px rgba(59,130,246,0.35),0 0 0 0 rgba(59,130,246,0);} }</style>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

/* =====================================================================
 * Helper: click-to-pin handler (only active in edit mode)
 * ===================================================================*/
const MapClickHandler = ({ enabled, onPick }) => {
  useMapEvents({
    click: (e) => {
      if (!enabled) return;
      onPick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
};

/* =====================================================================
 * Auto-recenter when target changes
 * ===================================================================*/
const Recenter = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
      map.flyTo(center, zoom ?? map.getZoom(), { duration: 0.8 });
    }
  }, [center, zoom, map]);
  return null;
};

/* =====================================================================
 * Walking mode hook — browser geolocation with throttled updates
 * Path is in-memory only (never sent to backend).
 * ===================================================================*/
const useWalkingMode = (enabled) => {
  const [position, setPosition] = useState(null);
  const [path, setPath] = useState([]);
  const [error, setError] = useState('');
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (!navigator.geolocation) {
      setError('Your browser does not support geolocation.');
      return;
    }
    setError('');
    setPath([]);
    setPosition(null);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const next = [pos.coords.latitude, pos.coords.longitude];
        setPosition(next);
        setPath((prev) => {
          // Only append if moved more than ~5 meters (rough threshold via lat-degree diff)
          const last = prev[prev.length - 1];
          if (!last || Math.abs(last[0] - next[0]) + Math.abs(last[1] - next[1]) > 0.00005) {
            return [...prev, next];
          }
          return prev;
        });
      },
      (err) => setError(err.message || 'Could not access your location.'),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => {
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled]);

  return { position, path, error, clearPath: () => setPath([]) };
};

/* =====================================================================
 * Address geocode search box (used in the zone edit panel)
 * ===================================================================*/
const GeocodeSearch = ({ onSelect }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true); setError(''); setResults([]);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/outreach/geocode`,
        { address: q.trim() }, { withCredentials: true });
      setResults(r.data?.results || []);
      if ((r.data?.results || []).length === 0) setError('No matches.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Geocoder error');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
          placeholder="Look up an address (e.g. Atlantic Ave, Brooklyn)"
          className="flex-1 px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
          data-testid="map-geocode-input" />
        <button onClick={search} disabled={loading || !q.trim()}
          className="text-[11px] px-2 py-1 bg-[#0A0A0A] text-white rounded hover:bg-[#333] disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="map-geocode-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          Search
        </button>
      </div>
      {error && <p className="text-[11px] text-red-600">{error}</p>}
      {results.length > 0 && (
        <ul className="border border-[#E5E7EB] rounded divide-y divide-[#F1F5F9] max-h-40 overflow-y-auto bg-white"
          data-testid="map-geocode-results">
          {results.map((r, i) => (
            <li key={i}>
              <button onClick={() => { onSelect(r); setResults([]); setQ(r.display_name); }}
                className="w-full text-left px-2 py-1.5 hover:bg-[#F8F9FA] text-[11px]"
                data-testid={`map-geocode-result-${i}`}>
                <span className="text-[#0A0A0A]">{r.display_name}</span>
                <span className="block text-[10px] text-[#9CA3AF]">{r.latitude.toFixed(5)}, {r.longitude.toFixed(5)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* Forces Leaflet to recompute its size on mount + window resize. Fixes
 * tiles that render before the container has its final dimensions (common
 * when MapContainer mounts inside a previously-hidden tab/drawer). */
const InvalidateSizeOnMount = () => {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    // Two passes: one quick, one after layout settles
    const t1 = setTimeout(fix, 50);
    const t2 = setTimeout(fix, 350);
    window.addEventListener('resize', fix);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', fix);
    };
  }, [map]);
  return null;
};

/* =====================================================================
 * Main map sub-tab
 * ===================================================================*/
const OutreachMap = ({ zones, locations = [], canWrite, onSaveGeo, onOpenLogForm }) => {
  const [editingZoneId, setEditingZoneId] = useState(null);
  const [pendingPin, setPendingPin] = useState(null); // {latitude, longitude} unsaved
  const [pendingPolygon, setPendingPolygon] = useState(null); // GeoJSON Polygon (unsaved)
  const [drawingPolygon, setDrawingPolygon] = useState(false);
  const [walking, setWalking] = useState(false);
  const [flyTo, setFlyTo] = useState(null);
  // Per-instance random key avoids "Map container is already initialized" under
  // React.StrictMode (instance is recreated on remount; useRef gets fresh value).
  const mapKeyRef = useRef(`leaflet-${Math.random().toString(36).slice(2)}`);
  const featureGroupRef = useRef(null);
  const walking_ = useWalkingMode(walking);

  const editingZone = useMemo(
    () => zones.find(z => z.id === editingZoneId) || null,
    [zones, editingZoneId],
  );

  // Pre-compute center: position > editing zone > first geo'd zone > default
  const center = useMemo(() => {
    if (walking_.position) return walking_.position;
    if (editingZone?.latitude && editingZone?.longitude) {
      return [editingZone.latitude, editingZone.longitude];
    }
    const geo = zones.find(z => z.latitude && z.longitude);
    if (geo) return [geo.latitude, geo.longitude];
    return DEFAULT_CENTER;
  }, [walking_.position, editingZone, zones]);

  const handlePickFromMap = useCallback((coords) => {
    if (!editingZoneId) return;
    setPendingPin(coords);
  }, [editingZoneId]);

  const handlePickFromGeocoder = (r) => {
    setPendingPin({ latitude: r.latitude, longitude: r.longitude });
    setFlyTo([r.latitude, r.longitude]);
  };

  const savePin = async () => {
    if (!editingZoneId || !pendingPin) return;
    await onSaveGeo(editingZoneId, pendingPin);
    setPendingPin(null);
    setEditingZoneId(null);
    setPendingPolygon(null);
    setDrawingPolygon(false);
  };

  const savePolygon = async () => {
    if (!editingZoneId || !pendingPolygon) return;
    await onSaveGeo(editingZoneId, { polygon_geojson: pendingPolygon });
    setPendingPolygon(null);
    setDrawingPolygon(false);
  };

  const clearPolygon = async () => {
    if (!editingZoneId) return;
    if (!window.confirm('Clear this zone\u2019s boundary polygon?')) return;
    await onSaveGeo(editingZoneId, { polygon_geojson: null });
    setPendingPolygon(null);
    setDrawingPolygon(false);
  };

  const recenterOnMe = () => {
    if (!walking_.position) return;
    setFlyTo([...walking_.position]);
  };

  return (
    <div className="space-y-3" data-testid="outreach-map-pane">
      {/* Map toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setWalking(w => !w)}
          className={`text-xs px-3 py-2 rounded inline-flex items-center gap-1.5 ${
            walking ? 'bg-blue-600 text-white hover:bg-blue-700' : 'border border-[#E5E7EB] hover:bg-[#F8F9FA]'
          }`}
          data-testid="map-walking-toggle">
          <Footprints className="w-3.5 h-3.5" />
          {walking ? 'Walking mode ON' : 'Walking mode'}
        </button>
        {walking && walking_.position && (
          <button onClick={recenterOnMe}
            className="text-xs px-2 py-2 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] inline-flex items-center gap-1"
            data-testid="map-recenter-me">
            <Crosshair className="w-3 h-3" /> Center on me
          </button>
        )}
        {walking && walking_.path.length > 0 && (
          <button onClick={walking_.clearPath}
            className="text-xs px-2 py-2 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA]"
            data-testid="map-clear-path">
            Clear path
          </button>
        )}
        <div className="flex-1" />
        <p className="text-[11px] text-[#9CA3AF]">
          Tip: click a zone marker to see details. {canWrite ? 'Use Edit position to drop or move a pin.' : ''}
        </p>
      </div>

      {walking_.error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 rounded">
          {walking_.error}
        </div>
      )}

      {/* Edit-pin tray */}
      {editingZone && (
        <div className="bg-white border border-[#BA1826]/30 rounded-lg p-3 space-y-2"
          data-testid="map-edit-tray">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#0A0A0A]">
              Setting position for <span className="text-[#BA1826]">{editingZone.name}</span>
            </p>
            <button onClick={() => { setEditingZoneId(null); setPendingPin(null); }}
              className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="map-edit-cancel">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] text-[#4B5563]">Click anywhere on the map to drop a pin, or look up an address:</p>
          <GeocodeSearch onSelect={handlePickFromGeocoder} />
          {pendingPin && (
            <div className="flex items-center justify-between bg-[#FFF8F8] border border-[#BA1826]/30 rounded p-2"
              data-testid="map-pending-pin">
              <span className="text-[11px] text-[#0A0A0A]">
                New pin: <b>{pendingPin.latitude.toFixed(5)}, {pendingPin.longitude.toFixed(5)}</b>
              </span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setPendingPin(null)}
                  className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA]">
                  Discard
                </button>
                <button onClick={savePin}
                  className="text-[11px] px-3 py-1 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1"
                  data-testid="map-pending-save">
                  <MapPin className="w-3 h-3" /> Save pin
                </button>
              </div>
            </div>
          )}

          {/* Polygon boundary controls */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#E5E7EB]">
            <p className="text-[11px] text-[#4B5563]">
              Boundary polygon: {editingZone.polygon_geojson ? <b className="text-green-700">set</b> : <span className="text-[#9CA3AF]">none</span>}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button onClick={() => setDrawingPolygon(d => !d)}
                className={`text-[11px] px-2 py-1 rounded inline-flex items-center gap-1 ${
                  drawingPolygon ? 'bg-blue-600 text-white' : 'border border-blue-600 text-blue-700 hover:bg-blue-50'
                }`}
                data-testid="map-polygon-toggle">
                <Pentagon className="w-3 h-3" /> {drawingPolygon ? 'Drawing…' : 'Draw boundary'}
              </button>
              {pendingPolygon && (
                <button onClick={savePolygon}
                  className="text-[11px] px-2 py-1 bg-[#BA1826] text-white rounded hover:bg-[#9F1421]"
                  data-testid="map-polygon-save">
                  Save polygon
                </button>
              )}
              {editingZone.polygon_geojson && !pendingPolygon && (
                <button onClick={clearPolygon}
                  className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded text-red-700 hover:bg-red-50"
                  data-testid="map-polygon-clear">
                  Clear polygon
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden" style={{ height: 520 }}
        data-testid="outreach-leaflet-map">
        <MapContainer key={mapKeyRef.current} center={center} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }}>
          <InvalidateSizeOnMount />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          <Recenter center={flyTo || (walking_.position && walking) ? (flyTo || walking_.position) : center}
            zoom={flyTo ? 15 : undefined} />

          <MapClickHandler enabled={!!editingZoneId && !drawingPolygon} onPick={handlePickFromMap} />

          {/* Existing zone polygons */}
          {zones.filter(z => z.polygon_geojson?.coordinates?.[0]).map(z => {
            // GeoJSON is [lng, lat]; Leaflet wants [lat, lng]
            const coords = z.polygon_geojson.coordinates[0].map(([lng, lat]) => [lat, lng]);
            return (
              <Polygon key={`poly-${z.id}`} positions={coords}
                pathOptions={{ color: z.color_tag || '#BA1826', fillColor: z.color_tag || '#BA1826', fillOpacity: 0.18, weight: 2 }} />
            );
          })}

          {/* Leaflet-draw control for boundary polygon (only while drawing) */}
          {canWrite && editingZoneId && drawingPolygon && (
            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topright"
                onCreated={(e) => {
                  if (e.layerType !== 'polygon') return;
                  const latlngs = e.layer.getLatLngs()[0];
                  // Build GeoJSON Polygon (close ring + flip to [lng, lat])
                  const ring = latlngs.map((p) => [p.lng, p.lat]);
                  ring.push(ring[0]);
                  setPendingPolygon({ type: 'Polygon', coordinates: [ring] });
                }}
                draw={{
                  polygon: { allowIntersection: false, showArea: false,
                    shapeOptions: { color: editingZone?.color_tag || '#BA1826' } },
                  rectangle: false, circle: false, marker: false,
                  circlemarker: false, polyline: false,
                }}
                edit={{ remove: true }}
              />
            </FeatureGroup>
          )}

          {/* Zone markers */}
          {zones.filter(z => z.latitude && z.longitude).map(z => (
            <Marker key={z.id} position={[z.latitude, z.longitude]}
              icon={coloredIcon(z.color_tag)}>
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>{z.name}</p>
                  <p style={{ margin: '2px 0', fontSize: 11, color: '#4B5563' }}>
                    {[z.neighborhood, z.city, z.state].filter(Boolean).join(' · ') || '—'}
                  </p>
                  <p style={{ margin: '2px 0 8px', fontSize: 11, color: '#9CA3AF' }}>
                    {z.log_count || 0} log{z.log_count === 1 ? '' : 's'}
                  </p>
                  {onOpenLogForm && (
                    <button onClick={() => onOpenLogForm(z.id)}
                      style={{ background: '#BA1826', color: 'white', border: 'none',
                        borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      data-testid={`map-zone-log-${z.id}`}>
                      <span style={{ fontSize: 12 }}>♥</span> Log from here
                    </button>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Pending pin preview */}
          {pendingPin && (
            <CircleMarker center={[pendingPin.latitude, pendingPin.longitude]}
              pathOptions={{ color: '#BA1826', fillColor: '#BA1826', fillOpacity: 0.3 }}
              radius={10} />
          )}

          {/* Location pins (distinct square markers, color-coded by permission_status) */}
          {locations.filter(lo => lo.latitude && lo.longitude).map((lo) => (
            <Marker key={`loc-${lo.id}`} position={[lo.latitude, lo.longitude]}
              icon={locationIcon(null, lo.permission_status)}>
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <p style={{ margin: 0, fontWeight: 700 }}>{lo.name}</p>
                  <p style={{ margin: '2px 0', fontSize: 11, color: '#4B5563' }}>
                    {lo.location_type?.replace(/_/g, ' ')}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: 11 }}>
                    <b>{(lo.permission_status || '').replace(/_/g, ' ')}</b>
                  </p>
                  {lo.address && <p style={{ margin: '2px 0', fontSize: 10, color: '#9CA3AF' }}>{lo.address}</p>}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Walking mode: live position + breadcrumb path */}
          {walking && walking_.position && (
            <Marker position={walking_.position} icon={youAreHereIcon} />
          )}
          {walking && walking_.path.length > 1 && (
            <Polyline positions={walking_.path}
              pathOptions={{ color: '#3B82F6', weight: 4, opacity: 0.7, dashArray: '6 6' }} />
          )}
          {walking && walking_.position && (
            <Circle center={walking_.position} radius={50}
              pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.08 }} />
          )}
        </MapContainer>
      </div>

      {/* Zone control list */}
      <div className="border border-[#E5E7EB] rounded-lg bg-white" data-testid="map-zone-list">
        <div className="px-3 py-2 border-b border-[#E5E7EB] flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Zones on map</p>
          <p className="text-[10px] text-[#9CA3AF]">{zones.filter(z => z.latitude && z.longitude).length} of {zones.length} placed</p>
        </div>
        {zones.length === 0 ? (
          <p className="px-3 py-4 text-xs text-[#9CA3AF] text-center">Create a zone first under the "Zones &amp; Streets" sub-tab.</p>
        ) : (
          <ul className="divide-y divide-[#F1F5F9]">
            {zones.map(z => {
              const placed = !!(z.latitude && z.longitude);
              return (
                <li key={z.id} className="px-3 py-2 flex items-center gap-3"
                  data-testid={`map-zone-row-${z.id}`}>
                  <span className="inline-block w-3 h-3 rounded-full border border-white shadow"
                    style={{ background: z.color_tag || '#BA1826' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#0A0A0A] truncate">{z.name}</p>
                    <p className="text-[10px] text-[#9CA3AF] truncate">
                      {placed
                        ? `${z.latitude.toFixed(4)}, ${z.longitude.toFixed(4)}`
                        : 'No position set'}
                    </p>
                  </div>
                  {placed && (
                    <button onClick={() => setFlyTo([z.latitude, z.longitude])}
                      className="text-[11px] px-2 py-0.5 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] inline-flex items-center gap-1"
                      data-testid={`map-zone-focus-${z.id}`}>
                      <Eye className="w-3 h-3" /> Focus
                    </button>
                  )}
                  {canWrite && (
                    <button onClick={() => { setEditingZoneId(z.id); setPendingPin(null); if (placed) setFlyTo([z.latitude, z.longitude]); }}
                      className={`text-[11px] px-2 py-0.5 rounded inline-flex items-center gap-1 ${
                        editingZoneId === z.id ? 'bg-[#BA1826] text-white' : 'border border-[#BA1826] text-[#BA1826] hover:bg-[#BA1826] hover:text-white'
                      }`}
                      data-testid={`map-zone-edit-${z.id}`}>
                      <Navigation className="w-3 h-3" /> {placed ? 'Move pin' : 'Set position'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default OutreachMap;
